import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import { apiClient } from '../services/api/apiClient';
import { useAuth } from './AuthContext';

interface WebSocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  joinFeedRoom: (room: string) => void;
  leaveFeedRoom: (room: string) => void;
  onNewJournal: (handler: (journal: unknown) => void) => void;
  offNewJournal: (handler: (journal: unknown) => void) => void;
}

const noop = () => {};

const WebSocketContext = createContext<WebSocketContextValue>({
  socket: null,
  isConnected: false,
  joinFeedRoom: noop,
  leaveFeedRoom: noop,
  onNewJournal: noop,
  offNewJournal: noop,
});

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!user) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      return;
    }

    const token = apiClient.getAccessToken() ?? undefined;
    const wsUrl = import.meta.env.VITE_WS_URL ?? 'http://localhost:3001';

    const socketInstance = io(wsUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketInstance.on('connect', () => setIsConnected(true));
    socketInstance.on('disconnect', () => setIsConnected(false));

    setSocket(socketInstance);
    return () => {
      socketInstance.disconnect();
    };
  }, [user?.id]);

  const joinFeedRoom = useCallback(
    (room: string) => {
      socket?.emit('join:room', room);
    },
    [socket],
  );

  const leaveFeedRoom = useCallback(
    (room: string) => {
      socket?.emit('leave:room', room);
    },
    [socket],
  );

  const onNewJournal = useCallback(
    (handler: (journal: unknown) => void) => {
      socket?.on('journal:new', handler);
    },
    [socket],
  );

  const offNewJournal = useCallback(
    (handler: (journal: unknown) => void) => {
      socket?.off('journal:new', handler);
    },
    [socket],
  );

  return (
    <WebSocketContext.Provider
      value={{ socket, isConnected, joinFeedRoom, leaveFeedRoom, onNewJournal, offNewJournal }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
